<template>
  <div class="page-container">
    <BillboardCarouselContainer
      in-viewport-root-margin="-35% 0%"
      @show-modal="showModal"
    />
    <QuickLinksContainer in-viewport-root-margin="-25% 0%" />
    <SignInBanner v-if="!loggedIn" in-viewport-root-margin="-25% 0%" />
    <FeatureCarousel v-if="featureCarousel" :carousel="featureCarousel" />
    <HomePageCarouselContainer v-if="$device.isMobile" />
    <ShopByCampaignContainer
      v-if="!$device.isMobile"
      in-viewport-root-margin="-25% 0%"
    />
    <ContentBannerListContainer in-viewport-root-margin="-25% 0%" />
    <ContentBannerFeaturedListContainer in-viewport-root-margin="-25% 0%" />
    <HomePageCarouselContainer v-if="$device.isDesktopOrTablet" />
    <ShopByCampaignContainer
      v-if="$device.isMobile"
      in-viewport-root-margin="-25% 0%"
    />
    <SchemaOrganization />
    <SchemaWebSite />
    <WelcomeMat />
    <client-only>
      <portal v-if="displayModal" to="modal-container">
        <transition appear name="fade">
          <Modal :title="modalTitle" :separator="true" @close="closeModal">
            <p>{{ modalMessage }}</p>
          </Modal>
        </transition>
      </portal>
    </client-only>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Modal from '@sephora-asia/core-modal/Modal.vue'
import BillboardCarouselContainer from '../components/BillboardCarouselContainer.vue'
import ContentBannerListContainer from '../components/ContentBannerListContainer.vue'
import ContentBannerFeaturedListContainer from '../components/ContentBannerFeaturedListContainer.vue'
import FeatureCarousel from '../components/FeatureCarousel.vue'
import HomePageCarouselContainer from '../components/HomePageCarouselContainer.vue'
import QuickLinksContainer from '../components/QuickLinksContainer.vue'
import SchemaOrganization from '../components/SchemaOrganization'
import SchemaWebSite from '../components/SchemaWebSite'
import ShopByCampaignContainer from '../components/ShopByCampaignContainer.vue'
import SignInBanner from '../components/SignInBanner.vue'
import WelcomeMat from '../components/WelcomeMat.vue'

export default {
  name: 'Index',

  components: {
    BillboardCarouselContainer,
    ContentBannerListContainer,
    ContentBannerFeaturedListContainer,
    FeatureCarousel,
    HomePageCarouselContainer,
    Modal,
    QuickLinksContainer,
    SchemaWebSite,
    SchemaOrganization,
    ShopByCampaignContainer,
    SignInBanner,
    WelcomeMat
  },

  data() {
    return {
      displayModal: false,
      modalTitle: '',
      modalMessage: ''
    }
  },

  async fetch() {
    const fetchList = [
      this.$store.dispatch('billboard/fetchAll'),
      this.$store.dispatch('homePageCarousel/fetchCarousels'),
      this.$store.dispatch('featureCarousel/fetchLive'),
      this.$store.dispatch('quickLink/fetchQuickLinks'),
      this.$store.dispatch('shopByCampaign/fetchAll')
    ]

    if (this.loggedIn) {
      fetchList.push(this.$store.dispatch('waitlist/fetchWaitlist'))
    }

    await Promise.all(fetchList)
  },

  head() {
    let meta = [
      {
        name: 'description',
        content: this.$t('layout.seoMeta.description'),
        hid: 'description',
        id: 'meta-description'
      },
      {
        name: 'keywords',
        content: this.$t('layout.seoMeta.keywords'),
        id: 'meta-keywords'
      },
      {
        property: 'og:title',
        content: this.$t('layout.seoMeta.title'),
        hid: 'og:title',
        id: 'og-title'
      },
      {
        property: 'og:description',
        content: this.$t('layout.seoMeta.description'),
        hid: 'og:description',
        id: 'og-description'
      },
      {
        property: 'og:url',
        content: this.website.domain,
        id: 'og-url',
        name: 'og:url'
      },
      { property: 'fb:page_id', content: this.seoMeta.fbPageId },
      { property: 'fb:app_id', content: this.seoMeta.fbAppId }
    ]
    if (this.hasAndroidApp) {
      meta = meta.concat([
        {
          property: 'al:android:url',
          content: 'sephora://',
          id: 'al-android-url'
        },
        {
          property: 'al:android:package',
          content: this.seoMeta.androidAppPackage
        },
        {
          property: 'al:android:app_name',
          content: this.seoMeta.androidAppName
        }
      ])
    }
    if (this.hasIosApp) {
      meta = meta.concat([
        { property: 'al:ios:url', content: 'sephora://', id: 'al-ios-url' },
        {
          property: 'al:ios:app_store_id',
          content: this.seoMeta.iosAppStoreId
        },
        { property: 'al:ios:app_name', content: this.seoMeta.iosAppName }
      ])
    }
    return {
      title: this.$t('layout.seoMeta.title'),
      meta,
      link: [
        {
          href: this.$i18n.canonicalizeUrl(
            `https://${this.website.domain}${this.$route.fullPath}`
          ),
          id: 'meta-canonical',
          rel: 'canonical'
        }
      ]
    }
  },

  computed: {
    ...mapGetters({
      hasAndroidApp: 'layoutConfig/hasAndroidApp',
      hasIosApp: 'layoutConfig/hasIosApp',
      loggedIn: 'user/loggedIn',
      seoMeta: 'globalConfig/seoMeta',
      userName: 'user/name',
      website: 'globalConfig/website',
      billboards: 'billboard/billboards',
      featureCarousel: 'featureCarousel/featureCarousel'
    })
  },

  watch: {
    loggedIn() {
      this.$store.dispatch('billboard/fetchAll')
    }
  },

  mounted() {
    this.notifyFromQueryParams()
  },

  methods: {
    _handleDataTracking({ product }) {
      this.$analytics.trackEvent('product_click', {
        product_id: product.id,
        variant_id: product.featuredVariant.id,
        product_tag: product?.label,
        product_highlight: '',
        product_seal: product.featuredVariant?.seal,
        origin: this.$nuxt?.context?.from?.path || document.referrer || ''
      })
    },

    notifyFromQueryParams() {
      if (this.$route.query && this.$route.query.message) {
        const query = Object.assign({}, this.$route.query)

        this.$notify({
          text: query.message,
          type: query.messageType
        })

        delete query.message
        delete query.messageType
        this.$router.replace({ query })
      }
    },
    showModal(termsAndConditions) {
      this.modalTitle = termsAndConditions.title
      this.modalMessage = termsAndConditions.description
      this.displayModal = true
      this.$nuxt.$emit('show-modal', true)
    },
    closeModal() {
      this.displayModal = false
      this.$nuxt.$emit('show-modal', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
